import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
// biome-ignore lint/correctness/noUndeclaredDependencies: iframe-resizer is included
import 'iframe-resizer/js/iframeResizer.contentWindow';

import { config } from './config';
import { main } from './configuration';
import { store } from './configuration/setup/store';
import { handleSilentSigninRedirectInIframe } from './configuration/login/redirect';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useDisplayMessages, useLocale } from './configuration/lang/langSlice';
import { DEFAULT_LOCALE } from './configuration/lang/lang';
import ErrorFallback from './components/ErrorFallback';
import { suspendPageLoad } from './routes/suspendPageLoad';
import AppLayout from './layout/AppLayout';
import { isRunningInIframe } from './utils';
import { CouldNotLoadApp } from './components/CouldNotLoadApp';
import { useLoadEulaStatusAndCommunicateWithParentIframe } from './hooks/iframeHooks';

const ManEula = suspendPageLoad(() => import('./pages/ManEula'));

const App = () => {
    const userLocale = useLocale();
    const displayMessages = useDisplayMessages();

    const { displayNull } = useLoadEulaStatusAndCommunicateWithParentIframe();

    if (!(displayMessages && userLocale)) {
        return null;
    }

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ErrorBoundary fallback={<ErrorFallback />}>
                {displayNull ? null : (
                    <AppLayout>
                        <ManEula />
                    </AppLayout>
                )}
            </ErrorBoundary>
        </IntlProvider>
    );
};

const renderApplication = (options?: { isErrorState?: boolean; errorState?: string }) => {
    const root = createRoot(document.getElementById('root') as HTMLElement);
    if (options?.isErrorState) {
        root.render(<CouldNotLoadApp errorState={options.errorState} />);
    } else {
        root.render(
            <Provider store={store}>
                <App />
            </Provider>
        );
    }
};

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;
const isProdPreview = import.meta.env.VITE_PRODUCTION_PREVIEW;

if ((isDev && config.enableMockServer) || isProdPreview) {
    import('../mocks/browser').then(({ startWorker }) => startWorker()).then(() => main(renderApplication));
}

const isRedirect = window.location.href.startsWith(config.login.redirectUri as string);
const runsInIframe = isRunningInIframe();

if (isRedirect && runsInIframe) {
    void handleSilentSigninRedirectInIframe();
} else if (isProd && !isProdPreview) {
    void main(renderApplication);
}
